import { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, LogOut, RefreshCw, Check, X, Clock } from "lucide-react";
import { toast } from "sonner";
import RedeemAtCounter from "../components/admin/RedeemAtCounter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "honto-admin-token";

function authClient(token) {
  return axios.create({
    baseURL: API,
    headers: { "Content-Type": "application/json", "X-Admin-Token": token },
  });
}

export default function AdminPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(false);
  const [pw, setPw] = useState("");

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        await authClient(token).get("/admin/founders", { params: { limit: 1 } });
        setAuthed(true);
      } catch {
        sessionStorage.removeItem(STORAGE_KEY);
        setToken("");
      }
    })();
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!pw.trim()) return;
    setChecking(true);
    try {
      await axios.post(`${API}/admin/login`, { token: pw });
      sessionStorage.setItem(STORAGE_KEY, pw);
      setToken(pw);
      setAuthed(true);
      toast.success("Welcome back.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Wrong password.");
    } finally {
      setChecking(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setToken("");
    setAuthed(false);
    setPw("");
  };

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-background" data-testid="admin-login-page">
        <form
          onSubmit={handleLogin}
          className="w-full max-w-sm rounded-3xl bg-white border border-border p-8 shadow-sm"
        >
          <p className="overline text-[#C47253] mb-3">Hontō · Admin</p>
          <h1 className="font-serif text-3xl text-[#1A2B22]">The back room</h1>
          <p className="text-sm text-[#5C7365] mt-2">One password to view founders &amp; verify pass payments.</p>
          <Label htmlFor="admin-pw" className="mt-7 block text-[10px] uppercase tracking-[0.18em] text-[#5C7365]">
            Password
          </Label>
          <Input
            id="admin-pw"
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            data-testid="admin-password-input"
            className="mt-2 h-11 rounded-xl bg-[#F7F5F0]"
            autoFocus
          />
          <Button
            type="submit"
            disabled={checking}
            data-testid="admin-login-btn"
            className="mt-5 w-full rounded-full bg-primary text-primary-foreground hover:bg-[#1F382B] h-11 text-xs tracking-[0.2em] uppercase disabled:opacity-50"
          >
            {checking ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Checking</> : "Enter"}
          </Button>
        </form>
      </div>
    );
  }

  return <AdminDashboard token={token} onLogout={handleLogout} />;
}

function AdminDashboard({ token, onLogout }) {
  const [founders, setFounders] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const client = useMemo(() => authClient(token), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [f, p] = await Promise.all([
        client.get("/admin/founders"),
        client.get("/admin/purchases"),
      ]);
      setFounders(f.data);
      setPurchases(p.data);
    } catch (err) {
      toast.error("Could not load admin data.");
    } finally {
      setLoading(false);
    }
  }, [client]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await client.patch(`/admin/purchases/${id}`, { status });
      setPurchases((prev) => prev.map((p) => (p.id === id ? { ...p, status } : p)));
      toast.success(`Marked ${status}`);
    } catch (err) {
      toast.error("Update failed.");
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground px-6 md:px-10 py-10" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto">
        <header className="flex items-center justify-between mb-10">
          <div>
            <p className="overline text-[#C47253]">Hontō · Admin</p>
            <h1 className="font-serif text-3xl md:text-4xl text-[#1A2B22] mt-1">The back room</h1>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={load}
              data-testid="admin-refresh"
              className="rounded-full h-10 px-4 text-xs tracking-[0.18em] uppercase"
            >
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            <Button
              variant="ghost"
              onClick={onLogout}
              data-testid="admin-logout"
              className="rounded-full h-10 px-4 text-xs tracking-[0.18em] uppercase"
            >
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </Button>
          </div>
        </header>

        <div className="grid grid-cols-3 gap-4 mb-8">
          <Stat label="Founders claimed" value={founders.length} />
          <Stat label="Pass purchases" value={purchases.length} />
          <Stat
            label="Pending verifications"
            value={purchases.filter((p) => p.status === "pending_verification").length}
            accent
          />
        </div>

        <Tabs defaultValue="purchases" data-testid="admin-tabs">
          <TabsList className="bg-[#EBEFDE]/60">
            <TabsTrigger value="purchases" data-testid="tab-purchases">Pass purchases</TabsTrigger>
            <TabsTrigger value="founders" data-testid="tab-founders">Founders</TabsTrigger>
            <TabsTrigger value="redeem" data-testid="tab-redeem">Redeem at counter</TabsTrigger>
          </TabsList>

          <TabsContent value="purchases" className="mt-5">
            <PurchasesTable purchases={purchases} loading={loading} onStatus={setStatus} />
          </TabsContent>
          <TabsContent value="founders" className="mt-5">
            <FoundersTable founders={founders} loading={loading} />
          </TabsContent>
          <TabsContent value="redeem" className="mt-5">
            <RedeemAtCounter client={client} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div
      className={[
        "rounded-2xl p-5 border",
        accent ? "bg-[#2C4C3B] text-[#F7F5F0] border-[#2C4C3B]" : "bg-white border-border",
      ].join(" ")}
      data-testid={`admin-stat-${label.replace(/\s+/g, "-").toLowerCase()}`}
    >
      <p className="text-[10px] uppercase tracking-[0.2em] opacity-70">{label}</p>
      <p className="font-serif text-4xl mt-2">{value}</p>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending_verification: { icon: Clock, cls: "bg-amber-100 text-amber-900", label: "Pending" },
    verified: { icon: Check, cls: "bg-emerald-100 text-emerald-900", label: "Verified" },
    rejected: { icon: X, cls: "bg-rose-100 text-rose-900", label: "Rejected" },
  };
  const c = map[status] || map.pending_verification;
  const Icon = c.icon;
  return (
    <Badge className={"rounded-full font-normal " + c.cls}>
      <Icon className="w-3 h-3 mr-1" /> {c.label}
    </Badge>
  );
}

function fmt(d) {
  return new Date(d).toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

function PurchasesTable({ purchases, loading, onStatus }) {
  if (loading) return <Loading />;
  if (purchases.length === 0) return <Empty msg="No pass purchases yet." />;
  return (
    <div className="overflow-x-auto rounded-2xl bg-white border border-border" data-testid="purchases-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-[#5C7365] bg-[#F1EEE6]">
            <th className="p-4">Customer</th>
            <th className="p-4">Tier</th>
            <th className="p-4">Amount</th>
            <th className="p-4">UTR</th>
            <th className="p-4">Submitted</th>
            <th className="p-4">Status</th>
            <th className="p-4 text-right">Action</th>
          </tr>
        </thead>
        <tbody>
          {purchases.map((p) => (
            <tr key={p.id} className="border-t border-border" data-testid={`purchase-row-${p.id}`}>
              <td className="p-4">
                <div className="font-medium text-[#1A2B22]">{p.name}</div>
                <div className="text-xs text-[#5C7365]">{p.email}</div>
                <div className="text-xs text-[#5C7365]">{p.phone}</div>
              </td>
              <td className="p-4 capitalize">{p.tier === "five" ? "5-bowl" : "10-bowl"}</td>
              <td className="p-4 font-serif">₹{p.amount_inr.toLocaleString("en-IN")}</td>
              <td className="p-4 font-mono text-xs">{p.utr}</td>
              <td className="p-4 text-xs text-[#5C7365]">{fmt(p.created_at)}</td>
              <td className="p-4"><StatusBadge status={p.status} /></td>
              <td className="p-4 text-right whitespace-nowrap">
                {p.status !== "verified" && (
                  <Button
                    size="sm"
                    onClick={() => onStatus(p.id, "verified")}
                    data-testid={`verify-btn-${p.id}`}
                    className="rounded-full bg-primary hover:bg-[#1F382B] h-8 text-[10px] tracking-[0.18em] uppercase mr-2"
                  >
                    Verify
                  </Button>
                )}
                {p.status !== "rejected" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onStatus(p.id, "rejected")}
                    data-testid={`reject-btn-${p.id}`}
                    className="rounded-full h-8 text-[10px] tracking-[0.18em] uppercase"
                  >
                    Reject
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FoundersTable({ founders, loading }) {
  if (loading) return <Loading />;
  if (founders.length === 0) return <Empty msg="No founders yet — share the link." />;
  return (
    <div className="overflow-x-auto rounded-2xl bg-white border border-border" data-testid="founders-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-[#5C7365] bg-[#F1EEE6]">
            <th className="p-4">#</th>
            <th className="p-4">Founder</th>
            <th className="p-4">Phone</th>
            <th className="p-4">First order</th>
            <th className="p-4">At-home interest</th>
            <th className="p-4">Joined</th>
          </tr>
        </thead>
        <tbody>
          {founders.map((f, i) => (
            <tr key={f.id} className="border-t border-border" data-testid={`founder-row-${f.id}`}>
              <td className="p-4 font-serif">{i + 1}</td>
              <td className="p-4">
                <div className="font-medium text-[#1A2B22]">{f.name}</div>
                <div className="text-xs text-[#5C7365]">{f.email}</div>
              </td>
              <td className="p-4 text-xs text-[#5C7365]">{f.phone || "—"}</td>
              <td className="p-4 text-xs text-[#5C7365]">{(f.survey?.q3_first_order || []).join(", ") || "—"}</td>
              <td className="p-4 text-xs text-[#5C7365]">{(f.survey?.q4_matcha_at_home || []).join(", ") || "—"}</td>
              <td className="p-4 text-xs text-[#5C7365]">{fmt(f.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Loading() {
  return (
    <div className="py-16 text-center text-[#5C7365]">
      <Loader2 className="w-5 h-5 animate-spin mx-auto" />
      <p className="mt-3 text-sm">Loading…</p>
    </div>
  );
}

function Empty({ msg }) {
  return <div className="py-16 text-center text-[#5C7365] text-sm">{msg}</div>;
}
